// +----------------------------------------------------------------------
// | EB [ EB赋能开发者，助力企业发展 ]
// +----------------------------------------------------------------------
// | Copyright (c) 2016~2024 https://www.eb.com All rights reserved.
// +----------------------------------------------------------------------
// | Licensed EB并不是自由软件，未经许可不能去掉EB相关版权
// +----------------------------------------------------------------------
// | Author: EB Team <admin@eb.com>
// +----------------------------------------------------------------------

/**
 * ⚠️ 注意：此文件为 EB 商户端配置文件
 * 
 * 主要用途：
 * 1. 为 EB 商户端相关组件提供 API 路径
 * 2. httpUrl: 前端页面跳转的基础 URL（不带 /api 前缀）
 * 3. https: API 请求的完整路径（带 /mer 前缀）
 * 
 * 与管理端的区别：
 * - 管理端 (tita_admin): baseURL = /api/admin
 * - 商户端 (tita_merchant): baseURL = /api/mer
 * 
 * 使用场景：
 * - 文件上传组件（uploadImg/uploadPicture）
 * - 商户相关功能
 * - WebSocket 连接
 * - iframe 嵌入页面
 */

import Cookies from "js-cookie";
import { getCrmebMerchantApiBase } from '@/config/crmebMerchantApi';
const getWsUrl = (): string => {
    const envWsUrl = import.meta.env.VITE_APP_WS_URL;

    if (envWsUrl) {
        return String(envWsUrl).trim().replace(/\/$/, '');
    }

    // 未显式配置时：优先用 API 根地址推导（开发 localhost:3000 → ws://localhost:3000）
    const apiUrl = import.meta.env.VITE_APP_API_URL;
    if (apiUrl) {
        return String(apiUrl)
            .trim()
            .replace(/\/$/, '')
            .replace(/^http:\/\//, 'ws://')
            .replace(/^https:\/\//, 'wss://');
    }

    // 根据协议自动选择 ws 或 wss，并保留当前页端口
    const protocol = location.protocol === 'https:' ? 'wss' : 'ws';
    const port = location.port;
    const portSuffix =
        port && port !== '80' && port !== '443' ? `:${port}` : '';
    return `${protocol}://${location.hostname}${portSuffix}`;
};

/**
 * 获取 用户前端 地址
 */
const getUserUrl = (): string | undefined => {
    const envWsUrl = import.meta.env.VITE_APP_USER_URL;

    if (envWsUrl) {
        return envWsUrl;
    }
    return undefined;
};

/**
 * 拼接用户端 H5 内嵌预览地址。
 * tita_user H5 为 history 路由（manifest router.mode=history），须 /pages/...，不要拼 /#/。
 * uni-app onLoad 靠 search query（如 formId）；缺斜杠时页面能开但参数常为空。
 */
export function buildUserH5PageUrl(pagePath: string, query = ''): string {
    const base = String(getUserUrl() || '').replace(/\/$/, '');
    const path = pagePath.replace(/^\//, '');
    const suffix = query ? `?${String(query).replace(/^\?/, '')}` : '';
    if (base) {
        return `${base}/${path}${suffix}`;
    }
    return `/${path}${suffix}`;
};

/**
 * 系统表单 H5 预览地址。
 * 对应 EB: /pages/admin/system_form/index?inner_frame=1&time=&form_id=
 * TITA query 仅驼峰：innerFrame / formId
 */
export function buildSystemFormPreviewUrl(formId: string | number): string {
    const time = Date.now() * 1000;
    return buildUserH5PageUrl(
        'pages/admin/system_form/index',
        `innerFrame=1&time=${time}&formId=${formId}`,
    );
}

/** 去掉首尾空白与引号、尾斜杠（用于 `VITE_APP_SER_URL` 等仅在 hostname 无法推导时的回退） */
const normalizeExternalUrl = (v: unknown): string => {
    if (v === undefined || v === null) return '';
    const s = String(v).trim();
    if (!s || s === 'undefined' || s === 'null') return '';
    return s.replace(/^["']+|["']+$/g, '').replace(/\/$/, '');
};

/**
 * 当前浏览器访问的 hostname（不含端口）。
 * 例：https://merchant.stage.titata.cn:8443 → merchant.stage.titata.cn
 */
export function getCurrentVisitHost(): string {
    if (typeof window === 'undefined' || !window.location) {
        return '';
    }
    return String(window.location.hostname || '').trim().toLowerCase();
}

/**
 * 当前浏览器 host（含端口，与 `location.host` 一致）。
 * 例：https://merchant.stage.titata.cn:8443 → merchant.stage.titata.cn:8443
 */
export function getCurrentVisitHostWithPort(): string {
    if (typeof window === 'undefined' || !window.location) {
        return '';
    }
    return String(window.location.host || '').trim().toLowerCase();
}

/**
 * 根据**当前浏览器 hostname** 推导客服根地址（titata 约定，协议与当前页一致）。
 *
 * - `merchant.*` → `kefu.` + 同后缀。
 * - `*.stage.titata.cn`（含 `m.stage.titata.cn`）→ `kefu.stage.titata.cn`。
 * - `m.titata.cn` 或 `*.titata.cn` 下以 `m.` 开头且不含 `.stage.` → `kefu.titata.cn`。
 * - `kefu.*`、localhost、127.0.0.1、其它未约定域名 → `undefined`（`getServiceUrl` 已优先读 `VITE_APP_SER_URL`，未配时再据此推导）。
 */
export function resolveKefuServiceUrlFromVisitHost(): string | undefined {
    const host = getCurrentVisitHost();
    if (!host || host === 'localhost' || host === '127.0.0.1') {
        return undefined;
    }
    if (host.startsWith('kefu.')) {
        return undefined;
    }
    if (typeof window === 'undefined' || !window.location) {
        return undefined;
    }
    const proto = window.location.protocol;

    if (host.startsWith('merchant.')) {
        const kefuHost = `kefu.${host.slice('merchant.'.length)}`;
        return `${proto}//${kefuHost}`;
    }

    if (host.endsWith('.stage.titata.cn')) {
        return `${proto}//kefu.stage.titata.cn`;
    }

    if (host === 'm.titata.cn' || (host.endsWith('.titata.cn') && !host.includes('.stage.') && /^m\./.test(host))) {
        return `${proto}//kefu.titata.cn`;
    }

    return undefined;
}

/** staging 构建下禁止回落到生产 kefu（逻辑同管理端 SettingMer） */
function isTitataStagingBundle(): boolean {
  return (
    import.meta.env.MODE === 'staging' ||
    import.meta.env.VITE_APP_ENV === 'staging' ||
    import.meta.env.VITE_APP_ENV === 'stage'
  );
}

function coerceStagingTitataServiceUrl(candidate: string | undefined): string | undefined {
  if (!isTitataStagingBundle() || !candidate) return candidate;
  try {
    const abs = /^https?:\/\//i.test(candidate) ? candidate : `https://${candidate}`;
    const u = new URL(abs);
    if (u.hostname === 'kefu.titata.cn') {
      u.hostname = 'kefu.stage.titata.cn';
      if (typeof window !== 'undefined' && window.location?.protocol === 'http:') {
        u.protocol = 'http:';
      } else {
        u.protocol = 'https:';
      }
      return u.origin.replace(/\/$/, '');
    }
  } catch {
    /* ignore */
  }
  return candidate;
}

/**
 * 客服前端根地址：**`VITE_APP_SER_URL`** → 域名推导；staging 包内禁止误用生产 `kefu.titata.cn`。
 */
const getServiceUrl = (): string | undefined => {
  const fromEnv = normalizeExternalUrl(import.meta.env.VITE_APP_SER_URL);
  const primary =
    fromEnv ||
    resolveKefuServiceUrlFromVisitHost() ||
    (isTitataStagingBundle() && typeof window !== 'undefined'
      ? `${window.location.protocol}//kefu.stage.titata.cn`
      : undefined);

  const coerced = coerceStagingTitataServiceUrl(primary);
  return coerced || undefined;
};

/**
 * 获取登录标题（从商户信息 Cookie 读取）
 */
const getLoginTitle = (): string => {
    try {
        const merInfo = Cookies.get('MerInfo');
        if (merInfo) {
            const parsed = JSON.parse(merInfo) as { loginTitle?: string };
            return parsed.loginTitle || '加载中...';
        }
    } catch (e) {
        console.warn('解析 MerInfo Cookie 失败:', e);
    }
    return '加载中...';
};

/**
 * EB 商户端配置
 * 
 * @property {string} httpUrl - 前端页面基础 URL（用于 window.open 跳转）
 * @property {string} https - API 请求完整路径（带 /mer 前缀，用于组件内直接请求）
 * @property {string} wsSocketUrl - WebSocket 连接地址
 * @property {string} title - 路由标题
 */
export interface SettingMerConfig {
    /** 前端页面基础 URL */
    httpUrl: string | undefined;
    /** 客服基础 URL */
    serviceUrl: string | undefined;
    /** API 请求完整路径（EB 组件等） */
    https: string;
    /** WebSocket 连接地址 */
    wsSocketUrl: string;
    /** 路由标题 */
    title: string;
}

const SettingMer: SettingMerConfig = {
    /**
     * 前端页面基础 URL
     * 用途：商户相关页面跳转、iframe 嵌入等
     * 示例：http://localhost:8083 或 https://merchant.titata.cn
     */
    httpUrl: getUserUrl(),

    /**
     * 客服基础 URL
     * 用途：商户相关页面跳转、iframe 嵌入等
     * 示例：http://localhost:8084 或 https://service.titata.cn,http://localhost/kefu
     */
    serviceUrl: getServiceUrl(),

    /**
     * API 请求完整路径（带 /mer 前缀）
     * 用途：文件上传、商户相关操作等 EB 商户端组件
     * 
     * ⚠️ 重要说明：
     * - 标准 API 请求（如 product、order 等）使用 /api/mer 前缀，在 request.ts 中配置
     * - EB 商户端 API（如 upload、merchant 等）使用 /api/mer 前缀，在此配置
     * 
     * 示例：
     * - 开发环境: /api/mer
     * - 测试环境: https://test.titata.cn/api/mer
     * - 生产环境: https://titata.cn/api/mer
     */
    https: getCrmebMerchantApiBase(),

    /**
     * WebSocket 连接地址
     * 用途：实时消息推送
     * 示例：ws://localhost 或 wss://titata.cn
     */
    wsSocketUrl: getWsUrl(),

    /**
     * 路由标题
     * 从商户信息 Cookie 中读取
     */
    title: getLoginTitle(),
};



// 开发环境日志（仅开发环境输出）
if (import.meta.env.MODE === 'development') {
    // console.log('📋 SettingMer 配置:');
    // console.log('  httpUrl:', SettingMer.httpUrl);
    // console.log('  serviceUrl:', SettingMer.serviceUrl);
    // console.log('  https:', SettingMer.https);
    // console.log('  wsSocketUrl:', SettingMer.wsSocketUrl);
    // console.log('  title:', SettingMer.title);
}

export default SettingMer;
